import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ClaimsStatus } from '@/components/ClaimsStatus';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Array<'admin' | 'instructor' | 'student'>;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles,
  redirectTo = '/login' 
}) => {
  const { user, userClaims, userProfile, loading, profileLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }
  
  // Check if user has custom claims (role and tenantId)
  const hasClaims = !!(userClaims?.role && userClaims?.tenantId);
  
  // If no claims yet, show the claims status component
  if (!hasClaims) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <ClaimsStatus />
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Need help? Contact support at support@digistore.com
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check role-based access
  if (allowedRoles && userClaims.role && !allowedRoles.includes(userClaims.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check tenant status
  // if (userProfile.tenant?.status === 'suspended') {
  //   return <Navigate to="/suspended" replace />;
  // }

  return <>{children}</>;
};